/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { keyExtractor } from '../../utils/PRIVATE_collectionUtils';
import { Cell } from '../../UNSAFE_TableView';

/**
 * Returns the cell type for the given cell element
 */
export const getCellType = (element: HTMLElement) => {
  return element.dataset['ojCellType'];
};

/**
 * Returns the row key for the given row element
 */
export const getRowKey = (element: HTMLElement) => {
  return element.dataset['ojKeyType'] === 'number'
    ? Number(element.dataset['ojKey'])
    : element.dataset['ojKey'];
};

/**
 * Returns the key for the row at the given index
 */
export const getRowKeyForRowIndex = (root: HTMLElement, tableId: string, index: number) => {
  if (root != null) {
    const rowElements = root.querySelectorAll(`[data-oj-table-data-row='${tableId}']`);
    if (rowElements.length > index) {
      return getRowKey(rowElements[index] as HTMLElement);
    }
  }
  return null;
};

/**
 * Returns the index of the given row element
 */
export const getRowIndexForRowElement = (
  root: HTMLElement,
  tableId: string,
  rowElement: HTMLElement
) => {
  if (root != null) {
    const rowElements = root.querySelectorAll(`[data-oj-table-data-row='${tableId}']`);
    const index = Array.prototype.indexOf.call(rowElements, rowElement);
    if (index > -1) {
      return index;
    }
  }
  return null;
};

/**
 * Returns the index of the row for the given key.
 */
export function getRowIndexForRowKey<K>(root: HTMLElement, tableId: string, rowKey: K) {
  if (root != null) {
    const rowElements = root.querySelectorAll(`[data-oj-table-data-row='${tableId}']`);
    for (let i = 0; i < rowElements.length; i++) {
      if (rowKey === getRowKey(rowElements[i] as HTMLElement)) {
        return i;
      }
    }
  }
  return null;
}

/**
 * Returns the column key for the given cell element
 */
export const getColumnKey = (element: HTMLElement) => {
  return element.dataset['ojColumnKeyType'] === 'number'
    ? Number(element.dataset['ojColumnKey'])
    : element.dataset['ojColumnKey'];
};

/**
 * Returns the key for the column at the given index
 */
export const getColumnKeyForColumnIndex = (root: HTMLElement, tableId: string, index: number) => {
  if (root != null) {
    const headerElements = root.querySelectorAll(`[data-oj-table-header-cell='${tableId}']`);
    if (headerElements.length > index) {
      return getColumnKey(headerElements[index] as HTMLElement);
    }
  }
  return null;
};

/**
 * Returns the index of the given column header element or footer element
 */
export const getColumnIndexForHeaderFooterElement = (
  root: HTMLElement,
  tableId: string,
  cellElement: HTMLElement,
  isHeader: boolean
) => {
  if (root != null) {
    const columnElements = root.querySelectorAll(
      isHeader
        ? `[data-oj-table-header-cell='${tableId}']`
        : `[data-oj-table-footer-cell='${tableId}']`
    );
    const index = Array.prototype.indexOf.call(columnElements, cellElement);
    if (index > -1) {
      return index;
    }
  }
  return null;
};

/**
 * Helper method to get the cell key definition from an element
 */
export function logicalCellExtractor<K, C>(
  element: HTMLElement,
  tableId: string,
  cellSelector: string,
  onlyAncestors?: boolean
): Cell<K, C> | null {
  const startingElement = onlyAncestors ? element.parentElement : element;
  if (startingElement != null) {
    const cellElement = startingElement.closest(cellSelector) as HTMLElement;
    if (cellElement != null) {
      const cellType = getCellType(cellElement);
      if (cellType === 'data') {
        return {
          rowKey: keyExtractor(cellElement, `[data-oj-table-data-row='${tableId}']`) as K,
          columnKey: getColumnKey(cellElement) as C,
          type: cellType
        };
      } else if (cellType === 'header' || cellType === 'footer') {
        return {
          columnKey: getColumnKey(cellElement) as C,
          type: cellType
        };
      } else if (cellType === 'noData') {
        return {
          type: cellType
        };
      }
    }
  }
  return null;
}

/**
 * Helper method to get an element from a cell descriptor
 */
export function getElementFromCell<K, C>(
  root: HTMLElement,
  tableId: string,
  cell: Cell<K, C>
): HTMLElement | null {
  if (cell.type === 'header') {
    // header case
    const headerElements = root.querySelectorAll(`[data-oj-table-header-cell='${tableId}']`);
    for (const headerElement of headerElements) {
      if (cell.columnKey === getColumnKey(headerElement as HTMLElement)) {
        return headerElement as HTMLElement;
      }
    }
  } else if (cell.type === 'footer') {
    // footer case
    const footerElements = root.querySelectorAll(`[data-oj-table-footer-cell='${tableId}']`);
    for (const footerElement of footerElements) {
      if (cell.columnKey === getColumnKey(footerElement as HTMLElement)) {
        return footerElement as HTMLElement;
      }
    }
  } else if (cell.type === 'data') {
    // data body case
    const rowElements = root.querySelectorAll(`[data-oj-table-data-row='${tableId}']`);
    for (const rowElement of rowElements) {
      if (cell.rowKey === getRowKey(rowElement as HTMLElement)) {
        const cellElements = rowElement.querySelectorAll(`[data-oj-table-focusable='${tableId}']`);
        for (const cellElement of cellElements) {
          if (cell.columnKey === getColumnKey(cellElement as HTMLElement)) {
            return cellElement as HTMLElement;
          }
        }
      }
    }
  } else if (cell.type === 'noData') {
    // no data case
    return root.querySelector(`[data-oj-table-nodata-cell='${tableId}']`) as HTMLElement;
  }
  return null;
}

/**
 * Helper method to get the top scroller offset of the Table
 */
export const getScrollerTopOffset = (root: HTMLElement, tableId: string) => {
  const headerRowElement = root.querySelector(
    `[data-oj-table-header-row='${tableId}']`
  ) as HTMLElement;
  return headerRowElement != null ? headerRowElement.getBoundingClientRect().height : 0;
};

/**
 * Helper method to get the bottom scroller offset of the Table
 */
export const getScrollerBottomOffset = (root: HTMLElement, tableId: string) => {
  const footerRowElement = root.querySelector(
    `[data-oj-table-footer-row='${tableId}']`
  ) as HTMLElement;
  return footerRowElement != null ? footerRowElement.getBoundingClientRect().height : 0;
};

/**
 * Returns the key of the first focusable row in the Table
 */
export const getFirstFocusableRowKey = (root: HTMLElement, tableId: string) => {
  if (root != null) {
    const firstRowElement = root.querySelector(`[data-oj-table-data-row='${tableId}']`);
    if (firstRowElement != null) {
      return getRowKey(firstRowElement as HTMLElement);
    }
  }
  return null;
};

/**
 * Returns the key of the last focusable row in the Table
 */
export const getLastFocusableRowKey = (root: HTMLElement, tableId: string) => {
  const rowElements = root.querySelectorAll(`[data-oj-table-data-row='${tableId}']`);
  const rowCount = rowElements.length;
  if (rowCount > 0) {
    return getRowKey(rowElements[rowCount - 1] as HTMLElement);
  }
  return null;
};

/**
 * Returns the cell definition of the first rendered cell in the Table
 */
export const getFirstFocusableCell = (root: HTMLElement, tableId: string) => {
  if (root != null) {
    const firstCellElement = root.querySelector(
      `[data-oj-table-focusable='${tableId}']`
    ) as HTMLElement;
    if (firstCellElement != null) {
      const firstCellType = getCellType(firstCellElement);
      if (firstCellType === 'header' || firstCellType === 'footer') {
        return { columnKey: getColumnKey(firstCellElement), type: firstCellType };
      } else if (firstCellType === 'noData') {
        return { type: 'noData' };
      }
      return {
        rowKey: getRowKey(firstCellElement),
        columnKey: getColumnKey(firstCellElement),
        type: 'data'
      };
    }
  }
  return null;
};

/**
 * Helper method to determine if the no data cell is present in the Table
 */
export const hasNoDataCell = (root: HTMLElement, tableId: string) => {
  if (root != null) {
    return root.querySelectorAll(`[data-oj-table-nodata-cell='${tableId}']`).length > 0;
  }
  return false;
};

/**
 * Helper method to determine if footer cells are present in the Table
 */
export const hasFooterCells = (root: HTMLElement, tableId: string) => {
  if (root != null) {
    return root.querySelectorAll(`[data-oj-table-footer-row='${tableId}']`).length > 0;
  }
  return false;
};
